import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { getLang } from "../../i18n";
import { ABS_COLORS } from "./common";

const WD = { fr: ["D", "L", "M", "M", "J", "V", "S"], en: ["S", "M", "T", "W", "T", "F", "S"] };

const blobError = async (e) => {
  const b = e?.response?.data;
  if (b instanceof Blob) {
    try { e.response.data = JSON.parse(await b.text()); } catch { /* corps non JSON */ }
  }
  return formatApiError(e);
};

const ExportBtn = ({ id, label, onClick, busy, disabled }) => (
  <button type="button" data-testid={id} onClick={onClick} disabled={busy || disabled}
    className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded-md text-sm font-semibold disabled:opacity-50">
    <FileSpreadsheet className="h-4 w-4" />{busy ? "Export…" : label}
  </button>
);

const Grid = ({ g }) => {
  if (!g.rows.length) return <p className="text-sm text-slate-500 py-10 text-center" data-testid="payroll-abs-empty">Aucune absence validée sur ce mois.</p>;
  const wd = WD[getLang()];
  const off = (d) => new Date(`${d}T12:00:00`).getDay() === 0 || g.holidays.includes(d);
  return (
    <div className="border border-slate-200 bg-white rounded-md overflow-x-auto" data-testid="payroll-abs-grid">
      <table className="text-xs whitespace-nowrap">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-[10px] font-bold text-slate-500">
            <th className="sticky left-0 bg-slate-50 px-2 py-2 text-left min-w-[170px] uppercase tracking-widest">Agent</th>
            {g.days.map((d) => <th key={d} className={`px-0.5 py-1 min-w-[28px] ${off(d) ? "bg-indigo-50 text-indigo-700" : ""}`}><div>{wd[new Date(`${d}T12:00:00`).getDay()]}</div><div>{d.slice(8)}</div></th>)}
            {g.legend.map((x) => <th key={x.short} className="px-1.5 py-1">{x.short}</th>)}
            <th className="px-2 py-1">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {g.rows.map((r) => (
            <tr key={r.agent_id} data-testid={`payroll-abs-row-${r.agent_id}`}>
              <td className="sticky left-0 bg-white px-2 py-1 font-semibold">{r.name}{r.matricule && <span className="ml-1 text-slate-400 font-normal">{r.matricule}</span>}</td>
              {g.days.map((d) => (
                <td key={d} className={`px-0.5 py-0.5 text-center ${off(d) ? "bg-indigo-50/50" : ""}`} data-testid={`payroll-abs-${r.agent_id}-${d}`}>
                  {r.by[d] && <span className={`inline-block px-1 rounded border text-[10px] font-bold ${ABS_COLORS[r.by[d]] || "bg-amber-100"}`}>{r.by[d]}</span>}
                </td>
              ))}
              {g.legend.map((x) => <td key={x.short} className="px-1.5 text-center font-mono">{r.totals[x.short] || ""}</td>)}
              <td className="px-2 text-center font-mono font-bold" data-testid={`payroll-abs-total-${r.agent_id}`}>{Object.values(r.totals).reduce((a, b) => a + b, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Legend = ({ g }) => (
  <div className="text-[11px] text-slate-500 mt-2 space-y-0.5" data-testid="payroll-abs-legend">
    <p>{g.legend.map((x) => <span key={x.short} className="mr-3"><b>{x.short}</b> = <span>{x.label}</span></span>)}</p>
    <p>Absences validées uniquement.</p>
    <p>Dans l'export client, le motif est remplacé par « Absent ».</p>
  </div>
);

export const PayrollAbsences = ({ month }) => {
  const [site, setSite] = useState("");
  const [sites, setSites] = useState([]);
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const req = useRef(0);
  useEffect(() => { http.get("/sites").then((r) => setSites(r.data.filter((s) => !s.archived))).catch((e) => toast.error(formatApiError(e))); }, []);
  const load = useCallback(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    const n = ++req.current;
    setG(null); setErr("");
    http.get("/payroll/absences", { params: { month, ...(site ? { site_id: site } : {}) } })
      .then((r) => { if (n === req.current) setG(r.data); })
      .catch((e) => { if (n === req.current) setErr(formatApiError(e)); });
  }, [month, site]);
  useEffect(() => { load(); }, [load]);
  const exp = async (variant) => {
    setBusy(variant);
    try {
      const r = await http.get("/payroll/absences.xlsx", { params: { month, variant, ...(site ? { site_id: site } : {}) }, responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href, download: `Absences_${variant === "client" ? "Client" : "Comptable"}_${month}.xlsx` }).click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      toast.success(variant === "client" ? "Export client téléchargé" : "Export comptable téléchargé");
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid="payroll-absences">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Site</div>
          <select data-testid="payroll-abs-site" value={site} onChange={(e) => setSite(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm min-w-[220px]">
            <option value="">Tous les sites</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="ml-auto flex gap-2 flex-wrap">
          <ExportBtn id="payroll-abs-export-accounting" label="Excel comptable (codes)" busy={busy === "accounting"} onClick={() => exp("accounting")} />
          <ExportBtn id="payroll-abs-export-client" label="Excel client (« Absent »)" busy={busy === "client"} disabled={!site} onClick={() => exp("client")} />
        </div>
      </div>
      {!site && <p className="text-[11px] text-slate-500 mb-2" data-testid="payroll-abs-client-hint">Choisissez un site pour l'export client. Aucun envoi automatique : le fichier est téléchargé, à transmettre vous-même.</p>}
      {err ? <p className="text-sm text-rose-700 py-10 text-center" data-testid="payroll-abs-error">Absences indisponibles : {err} <button type="button" data-testid="payroll-abs-retry" onClick={load} className="underline font-semibold">Réessayer</button></p>
        : g ? <Grid g={g} /> : <p className="text-sm text-slate-500 py-10 text-center" data-testid="payroll-abs-loading">Chargement…</p>}
      {g?.legend.length > 0 && <Legend g={g} />}
    </div>
  );
};
